import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaTimes, FaCalendarAlt, FaCheckCircle } from 'react-icons/fa';
import PageTemplate from '../../components/Common/PageTemplate';

const FedekAkreditasyonDegerlendirmeSureci = () => {
  const images = [
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fedek/ATY_0473.JPG',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fedek/ATY_0463.JPG',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fedek/ATY_0474.JPG',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fedek/ATY_0516.JPG',
    'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fedek/ATY_0514.JPG',
  ];

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = useCallback((index) => {
    setCurrentIndex(index);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (!modalOpen) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [modalOpen, closeModal, nextImage, prevImage]);

  const departments = ['Aktüerya Bilimleri', 'İstatistik', 'Kimya', 'Matematik'];

  return (
    <PageTemplate
      title="Fakültemizde FEDEK Akreditasyon Değerlendirme Süreci Gerçekleştirildi"
      subtitle=""
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-6 sm:p-8 md:p-12 rounded-3xl shadow-xl -mt-16 relative z-10 mb-16">

          {/* Üst bilgi satırı: Tarih + FEDEK badge */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
              <FaCalendarAlt className="text-gray-400" />
              <span>24.02.2026</span>
            </div>
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold bg-red-600 text-white tracking-wide shadow-sm">
              FEDEK Akreditasyon
            </span>
          </div>

          {/* Değerlendirilen Bölümler */}
          <div className="mb-8 p-5 sm:p-6 bg-red-50 border-l-4 border-red-600 rounded-2xl">
            <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-3">Değerlendirilen Bölümler</p>
            <div className="flex flex-wrap gap-2">
              {departments.map((dep) => (
                <span
                  key={dep}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold bg-white border border-red-200 text-red-700 shadow-sm"
                >
                  {dep}
                </span>
              ))}
            </div>
          </div>

          {/* Bilgi Kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FaCalendarAlt className="text-red-600 text-base" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Değerlendirme Tarihi</p>
                <p className="text-sm font-bold text-gray-800">8–10 Şubat 2026</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <FaCheckCircle className="text-red-600 text-base" />
              </div>
              <div>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Akreditasyon Kuruluşu</p>
                <p className="text-sm font-bold text-gray-800">FEDEK</p>
              </div>
            </div>
          </div>

          {/* Metin İçeriği */}
          <div className="space-y-6 mb-12">
            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              Fakültemiz bünyesinde yer alan <strong>Aktüerya Bilimleri, İstatistik, Kimya ve Matematik</strong> Bölümlerimiz için 8–10 Şubat tarihleri arasında yürütülen{' '}
              <span className="font-bold text-red-600">Fen, Edebiyat, Fen-Edebiyat, Dil ve Tarih-Coğrafya Fakülteleri Öğretim Programları Değerlendirme ve Akreditasyon Derneği (FEDEK)</span>{' '}
              akreditasyon değerlendirme süreci başarıyla tamamlanmıştır.
            </p>

            <div className="border-l-4 border-red-500 pl-5 sm:pl-6 py-1">
              <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                Değerlendirme süreci kapsamında <span className="font-semibold text-red-600">FEDEK</span> ekipleri, bölümlerimizde kapsamlı incelemelerde bulunmuş; eğitim-öğretim faaliyetleri, araştırma çalışmaları, kalite güvencesi uygulamaları ve kurumsal işleyiş çok yönlü olarak ele alınmıştır.
              </p>
            </div>

            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              Akademik ve idari personelimiz, öğrencilerimiz, mezunlarımız ve dış paydaşlarımız ile yüz yüze görüşmeler gerçekleştirilerek süreç bütüncül bir yaklaşımla yürütülmüştür. Ayrıca takım başkanları, Üniversitemiz Rektörlüğüne bağlı ilgili daire başkanları ile görüşmeler yaparak değerlendirmeyi kurumsal düzeyde de detaylı biçimde incelemiştir.
            </p>

            <div className="bg-gradient-to-r from-red-50 to-white border border-red-100 rounded-2xl p-5 sm:p-6">
              <p className="text-base sm:text-lg leading-relaxed text-gray-700">
                Fakültemiz, kalite odaklı eğitim anlayışı ve sürekli iyileştirme ilkesi doğrultusunda çalışmalarını kararlılıkla sürdürmektedir. Sürece katkı sağlayan tüm paydaşlarımıza teşekkür ederiz.
              </p>
            </div>
          </div>

          {/* Bölücü */}
          <div className="border-t border-gray-100 mb-10" />

          {/* Fotoğraf Galerisi */}
          <div className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((src, i) => (
                <div
                  key={i}
                  className="group relative rounded-2xl overflow-hidden shadow-md border border-gray-100 cursor-pointer aspect-[4/3]"
                  onClick={() => openModal(i)}
                >
                  <img
                    src={src}
                    alt={`FEDEK Akreditasyon Değerlendirme Süreci - ${i + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3 border border-white/40">
                      <div className="w-5 h-5 border-2 border-white rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Geri Dön Butonu */}
        <div className="text-center mb-16">
          <Link
            to="/haberler"
            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-gray-300"
          >
            <FaArrowLeft className="mr-3 -ml-1 h-5 w-5 group-hover:-translate-x-1 transition-transform duration-300" />
            Tüm Haberlere Geri Dön
          </Link>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center"
          onClick={closeModal}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-60"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 text-white bg-black/50 px-3 py-1 rounded-full text-sm z-60">
            {currentIndex + 1} / {images.length}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-2 sm:left-4 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 rounded-full p-3 z-60 transition-all"
          >
            <FaChevronLeft size={20} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-2 sm:right-4 text-white hover:text-gray-300 bg-black/50 hover:bg-black/70 rounded-full p-3 z-60 transition-all"
          >
            <FaChevronRight size={20} />
          </button>

          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[currentIndex]}
              alt={`FEDEK Akreditasyon Değerlendirme Süreci - ${currentIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </PageTemplate>
  );
};

export default FedekAkreditasyonDegerlendirmeSureci;
